import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTable, useSortBy, useFilters, usePagination } from "react-table";
import axios from "axios";
import { format } from "date-fns";
import Table from "../../components/tableComponent";
import CustomerCaseModal from "../../components/customerCaseModal";

const CustomerCasesTable = () => {
  /* ─────────────────────────────────────────────── */
  /*  Local state                                   */
  /* ─────────────────────────────────────────────── */
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCase, setSelectedCase] = useState(null);

  // UI-filter state
  const [filterInput, setFilterInput] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [showOpenOnly, setShowOpenOnly] = useState(true); // ← default ON

  /* ─────────────────────────────────────────────── */
  /*  Column definitions                             */
  /* ─────────────────────────────────────────────── */
  const columns = useMemo(
    () => [
      {
        Header: "Created",
        accessor: "createdAt",
        Cell: ({ value }) =>
          value && !isNaN(new Date(value))
            ? format(new Date(value), "dd.MM.yyyy")
            : "—",
      },
      {
        Header: "First outbound",
        accessor: "firstOutboundAt",
        Cell: ({ value }) =>
          value && !isNaN(new Date(value))
            ? format(new Date(value), "dd.MM.yyyy")
            : "—",
      },
      {
        Header: "Last outbound",
        accessor: "lastOutboundAt",
        Cell: ({ value }) =>
          value && !isNaN(new Date(value))
            ? format(new Date(value), "dd.MM.yyyy")
            : "—",
      },
      {
        Header: "Last inbound",
        accessor: "lastInboundAt",
        Cell: ({ value }) =>
          value && !isNaN(new Date(value))
            ? format(new Date(value), "dd.MM.yyyy")
            : "—",
      },
      { Header: "Serial Number", accessor: "serialNumber" },
      { Header: "Customer Name", accessor: "customer.fullName" },
      { Header: "Phone", accessor: "customer.phone" },
      { Header: "Email", accessor: "customer.email" },
      { Header: "Source", accessor: "source" },
      { Header: "Status", accessor: "caseStatus" },
    ],
    []
  );

  /* ─────────────────────────────────────────────── */
  /*  Fetch all customer cases                       */
  /* ─────────────────────────────────────────────── */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get("/api/customer-cases");
        setCases(data || []);
      } catch (err) {
        console.error("Error fetching customer cases:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* ─────────────────────────────────────────────── */
  /*  “Show only open cases” pre-filter               */
  /* ─────────────────────────────────────────────── */
  const filteredData = useMemo(() => {
    return showOpenOnly
      ? cases.filter((c) => c.caseStatus !== "Closed")
      : cases;
  }, [cases, showOpenOnly]);

  /* ─────────────────────────────────────────────── */
  /*  Table hooks                                    */
  /* ─────────────────────────────────────────────── */
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    previousPage,
    nextPage,
    setFilter,
    rows,
    state: { pageIndex, pageSize },
    setPageSize,
  } = useTable(
    {
      columns,
      data: filteredData, // ← passes the pre-filtered or full dataset
      initialState: {
        pageIndex: 0,
        pageSize: 50,
        sortBy: [{ id: "createdAt", desc: true }],
      },
    },
    useFilters,
    useSortBy,
    usePagination
  );

  /* ───────────────────────────────────────────────
       Row-colour logic
       ─ Yellow  → status === "Open" or "New"
       ─ Red     → status === "Due"
     ─────────────────────────────────────────────── */
  const prepareRowWithColor = useCallback(
    (row) => {
      prepareRow(row);

      const { caseStatus } = row.original;

      let colour = null;
      if (caseStatus === "New" || caseStatus === "Open") {
        colour = "bg-yellow-100";
      } else if (caseStatus === "Due") {
        colour = "bg-red-100";
      }

      if (colour) {
        const baseGetRowProps = row.getRowProps;
        row.getRowProps = (userProps = {}) => {
          const base = baseGetRowProps(userProps);
          return {
            ...base,
            className: [base.className, colour].filter(Boolean).join(" "),
          };
        };
      }
    },
    [prepareRow]
  );

  /* ─────────────────────────────────────────────── */
  /*  Column-specific text filter                    */
  /* ─────────────────────────────────────────────── */
  const onFilterInputChange = (e) => {
    setFilterInput(e.target.value);
    if (filterCategory) setFilter(filterCategory, e.target.value);
  };

  /* ─────────────────────────────────────────────── */
  /*  Checkbox handler                               */
  /* ─────────────────────────────────────────────── */
  const toggleOpenOnly = (e) => setShowOpenOnly(e.target.checked);

  /* ─────────────────────────────────────────────── */
  /*  Modal helpers                                  */
  /* ─────────────────────────────────────────────── */
  const handleRowClick = (row) => setSelectedCase(row.original);

  const updateLocalCase = useCallback(
    (updatedCase) =>
      setCases((prev) =>
        prev.map((c) => (c._id === updatedCase._id ? updatedCase : c))
      ),
    []
  );

  if (loading) return <div>Loading…</div>;

  return (
    <div className="flex-grow p-4">
      <h2 className="text-2xl font-semibold mb-4">Customer Cases</h2>

      {/* ——— Filter Controls ——— */}
      <div className="flex justify-between items-center mb-4">
        {/* Dropdown to select column for text filter */}
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="border p-2 rounded mr-2"
        >
          <option value="">Select Column…</option>
          {columns
            .filter((c) => typeof c.accessor === "string")
            .map((c) => (
              <option value={c.accessor} key={c.accessor}>
                {c.Header}
              </option>
            ))}
        </select>

        {/* Toggle to show only open cases (i.e. hide “Closed”) */}
        <label className="flex items-center space-x-2 bg-gray-200 px-3 py-1 rounded-lg cursor-pointer">
          <input
            type="checkbox"
            checked={showOpenOnly}
            onChange={toggleOpenOnly}
            className="form-checkbox text-black rounded border-gray-300"
          />
          <span className="text-sm font-medium text-gray-700">
            Show only open cases
          </span>
        </label>
      </div>

      {/* Text input filter for selected column */}
      <input
        value={filterInput}
        onChange={onFilterInputChange}
        placeholder="Enter filter value…"
        className="border p-2 rounded w-full mb-4"
      />

      {/* ——— Table ——— */}
      <Table
        columns={columns}
        data={page}
        onRowClick={handleRowClick}
        getTableProps={getTableProps}
        getTableBodyProps={getTableBodyProps}
        headerGroups={headerGroups}
        prepareRow={prepareRowWithColor}
        canPreviousPage={canPreviousPage}
        canNextPage={canNextPage}
        previousPage={previousPage}
        nextPage={nextPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        pageIndex={pageIndex}
        totalFilteredRows={rows.length}
      />

      {/* ——— Modal ——— */}
      {selectedCase && (
        <CustomerCaseModal
          isOpen={!!selectedCase}
          caseData={selectedCase}
          closeModal={() => setSelectedCase(null)}
          onCaseUpdated={updateLocalCase}
        />
      )}
    </div>
  );
};

export default CustomerCasesTable;
